import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, of, catchError, map } from 'rxjs';
import { environment } from 'src/environments/environment';
import { SsoCheckRequestModel } from '../models/sso-check-request.model';
import { SsoCheckResponseModel } from '../models/sso-check-response.model';
import { SsoStoreRequestModel } from '../models/sso-store-request.model';
import { SessionInfoModel } from '../models/session-info.model';
import { SsoLogoutModel } from '../models/sso-logout.model';

@Injectable({
  providedIn: 'root'
})
export class SsoService {
  private readonly http = inject(HttpClient);

  /**
   * Check if an existing SSO session can be reused for the current auth request.
   * If valid, returns a callback URL to complete the login without IDP redirect.
   */
  public checkSsoSession(authRequestId: string): Observable<SsoCheckResponseModel> {
    const request: SsoCheckRequestModel = { authRequestId };
    return this.http
      .post<SsoCheckResponseModel>(`${environment.api}/sso/check`, request, {
        withCredentials: true
      })
      .pipe(catchError(() => of({ hasValidSession: false })));
  }

  /**
   * Store the session info in cookies for future SSO use.
   * This should be called after a successful login.
   */
  public storeSession(sessionInfo: SessionInfoModel): Observable<boolean> {
    const request: SsoStoreRequestModel = {
      sessionId: sessionInfo.sessionId,
      sessionToken: sessionInfo.sessionToken
    };
    return this.http
      .post(`${environment.api}/sso/store`, request, {
        withCredentials: true
      })
      .pipe(
        map(() => true),
        catchError(() => of(false))
      );
  }

  /**
   * Clear SSO session cookies during logout.
   * Returns the Zitadel logout URL to redirect to.
   */
  public clearSession(model?: SsoLogoutModel): Observable<string | null> {
    return this.http
      .post<{ logoutUrl: string }>(
        `${environment.api}/sso/clear`,
        model,
        {
          withCredentials: true
        }
      )
      .pipe(
        map(response => response.logoutUrl),
        catchError(() => of(null))
      );
  }

  /**
   * Check if user has an active SSO session.
   * Returns true if HttpOnly session cookies exist on the server.
   */
  public getSsoStatus(): Observable<boolean> {
    return this.http
      .get<{ hasSession: boolean }>(`${environment.api}/sso/status`, {
        withCredentials: true
      })
      .pipe(
        map((response) => response.hasSession),
        catchError(() => of(false))
      );
  }
}
